import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Monitor, 
  Smartphone, 
  Globe, 
  LogOut, 
  ChevronDown, 
  ChevronRight, 
  Search, 
  X, 
  ShieldAlert, 
  CheckCircle2, 
  Clock, 
  Laptop,
  Layers,
  MapPin
} from 'lucide-react';
import { IdentityService } from '@/services/IdentityService';

export default function ActiveSessionsView({ globalSearch = '' }) {
  const [roleFilter, setRoleFilter] = useState('Semua');
  const [searchQuery, setSearchQuery] = useState(globalSearch);
  const [expandedUserIds, setExpandedUserIds] = useState(['usr_fadli_01', 'usr_noor_05']);
  const [selectedSessionDetail, setSelectedSessionDetail] = useState(null);

  // Active Sessions Data from IdentityService
  const [userSessions, setUserSessions] = useState(() => IdentityService.getActiveSessions());

  const toggleExpand = (userId) => {
    setExpandedUserIds(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleLogoutDevice = (userId, sessionId, deviceName) => {
    const updated = IdentityService.logoutSingleDevice(userId, sessionId);
    setUserSessions(updated);
    toast.info(`📱 Perangkat ${deviceName} berhasil dikeluarkan (Logout Device).`);
  };

  const handleForceLogoutAll = (userId, userName) => {
    const updated = IdentityService.forceLogoutAllDevices(userId);
    setUserSessions(updated);
    if (selectedSessionDetail?.userId === userId) {
      setSelectedSessionDetail(null);
    }
    toast.error(`🔒 Semua sesi login untuk ${userName} telah ditutup secara paksa!`);
  };

  const filteredUsers = userSessions.filter(u => {
    const matchesSearch = 
      u.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.company.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = roleFilter === 'Semua' || u.role.toLowerCase() === roleFilter.toLowerCase();
    return matchesSearch && matchesRole;
  });

  const totalUsersOnline = filteredUsers.length;
  const totalSessionsActive = filteredUsers.reduce((acc, u) => acc + u.devices.length, 0);

  return (
    <div className="p-6 space-y-6 font-sans bg-[#F8F8F6] min-h-screen text-slate-800">
      
      {/* ─── HEADER BAR ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-[#ECECEC] shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Active Sessions</h1>
            <span className="flex items-center gap-1 bg-emerald-50 text-[#20C05C] border border-emerald-200 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-[#20C05C] rounded-full animate-pulse"></span> {totalUsersOnline} USER ONLINE
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Pantau & kelola semua sesi pengguna yang sedang login di seluruh platform Satala.</p>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <Input 
            placeholder="Cari user, email, device..." 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-8 h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl"
          />
        </div>
      </div>

      {/* ─── 4 STAT CARDS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#20C05C]">USER ONLINE</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{totalUsersOnline}</h3>
            <p className="text-[11px] text-emerald-700 font-bold mt-2">⭐ +8% dari 10 menit lalu</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600">SESSION AKTIF</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{totalSessionsActive}</h3>
            <p className="text-[11px] text-slate-500 mt-2">Semua Perangkat Terhubung</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-600">LOGIN HARI INI</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">156</h3>
            <p className="text-[11px] text-slate-500 mt-2">Total Login Sukses</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-purple-600">AVERAGE DURATION</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">42m</h3>
            <p className="text-[11px] text-slate-500 mt-2">Rata-rata Durasi Sesi</p>
          </CardContent>
        </Card>
      </div>

      {/* ─── ROLE FILTER TABS ─── */}
      <div className="flex border-b border-[#ECECEC] gap-2 overflow-x-auto pb-0.5">
        {['Semua', 'Marketing', 'Office', 'Backend', 'Client'].map(role => (
          <button
            key={role}
            onClick={() => setRoleFilter(role)}
            className={`pb-2.5 px-4 text-xs font-bold transition-all border-b-2 ${
              roleFilter === role 
                ? 'border-[#A97838] text-[#A97838]' 
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {role} ({role === 'Semua' ? userSessions.length : userSessions.filter(u => u.role.toLowerCase() === role.toLowerCase()).length})
          </button>
        ))}
      </div>

      {/* ─── HIERARCHICAL TABLE: User -> Devices ─── */}
      <Card className="bg-white border-[#ECECEC] shadow-xs overflow-hidden">
        <div className="p-4 border-b border-[#ECECEC] flex justify-between items-center bg-[#FAF8F5]">
          <h3 className="font-bold text-xs text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#A97838]" /> Daftar Hierarki Sesi Pengguna & Perangkat
          </h3>
          <span className="text-[10px] font-bold text-slate-500 bg-white px-2.5 py-0.5 rounded border border-slate-200">Hierarchical View</span>
        </div>

        <div className="divide-y divide-[#ECECEC]">
          {filteredUsers.map(user => {
            const isExpanded = expandedUserIds.includes(user.userId);
            return (
              <div key={user.userId} className="transition-colors">
                
                {/* USER ROW (PARENT) */}
                <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white hover:bg-slate-50/80">
                  
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => toggleExpand(user.userId)} 
                      className="p-1 text-slate-400 hover:text-[#A97838] transition-colors"
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-[#A97838]" /> : <ChevronRight className="w-4 h-4" />}
                    </button>

                    <div className="w-9 h-9 rounded-full bg-[#302621] text-[#A97838] font-black text-xs flex items-center justify-center border border-[#A97838]">
                      {user.userName.charAt(0)}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-slate-900 text-xs">{user.userName}</h4>
                        <span className="text-[9px] font-extrabold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{user.role}</span>
                        <span className="text-[9px] font-extrabold bg-[#F4EFE6] text-[#A97838] px-2 py-0.5 rounded border border-[#ECECEC]">
                          {user.devices.length} Perangkat
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 font-mono mt-0.5">{user.email} • <span className="font-bold text-slate-600">{user.company}</span></p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <Button 
                      onClick={() => setSelectedSessionDetail(user)}
                      variant="outline"
                      className="h-8 text-[11px] font-bold border-[#ECECEC] hover:bg-slate-100 rounded-xl"
                    >
                      Session Detail
                    </Button>

                    <Button 
                      onClick={() => handleForceLogoutAll(user.userId, user.userName)}
                      variant="destructive"
                      className="h-8 text-[11px] font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl gap-1"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Force Logout All
                    </Button>
                  </div>
                </div>

                {/* DEVICE SUB-ROWS (CHILDREN) */}
                {isExpanded && (
                  <div className="bg-[#FAF8F5] p-3 sm:pl-12 space-y-2 border-t border-[#ECECEC]/60">
                    {user.devices.map((dev) => (
                      <div key={dev.sessionId} className="bg-white p-3 rounded-xl border border-[#ECECEC] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs shadow-2xs hover:border-[#A97838] transition-colors">
                        
                        <div className="flex items-center gap-3">
                          {dev.device.includes('iPhone') || dev.device.includes('Mobile') ? (
                            <Smartphone className="w-5 h-5 text-[#A97838]" />
                          ) : (
                            <Monitor className="w-5 h-5 text-slate-600" />
                          )}
                          
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-900">{dev.device} • {dev.browser}</p>
                              {dev.isCurrent && <span className="text-[9px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded">Perangkat Saat Ini</span>}
                            </div>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                              IP: {dev.ip} • Lokasi: {dev.location} • Halaman: <span className="text-[#A97838] font-semibold">{dev.currentPage}</span>
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 self-end sm:self-auto">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            dev.status === 'Online' ? 'bg-emerald-50 text-[#20C05C] border border-emerald-200' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {dev.status}
                          </span>

                          <button 
                            onClick={() => handleLogoutDevice(user.userId, dev.sessionId, dev.device)}
                            className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 px-2.5 py-1 rounded-lg transition-colors border border-rose-200"
                          >
                            Logout Device
                          </button>
                        </div>

                      </div>
                    ))}
                  </div>
                )}

              </div>
            );
          })}
        </div>
      </Card>

      {/* ─── SLIDE-OVER DRAWER MODAL: SESSION DETAIL ─── */}
      {selectedSessionDetail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 -xs animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-250 border-l border-[#ECECEC]">
            
            {/* Header Drawer */}
            <div className="p-5 border-b border-[#ECECEC] bg-[#FAF8F5] flex justify-between items-center">
              <div>
                <h3 className="font-black text-slate-900 text-base">{selectedSessionDetail.userName}</h3>
                <p className="text-xs text-slate-500">{selectedSessionDetail.email} • <span className="font-bold text-[#A97838]">{selectedSessionDetail.role}</span></p>
              </div>
              <button onClick={() => setSelectedSessionDetail(null)} className="p-2 text-slate-400 hover:text-slate-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Drawer */}
            <div className="p-5 overflow-y-auto space-y-6 flex-1 text-xs">
              
              {/* Ringkasan Session */}
              <div className="bg-slate-50 border border-[#ECECEC] p-4 rounded-xl space-y-2">
                <h4 className="font-bold text-slate-800 text-xs border-b border-slate-200 pb-2">Ringkasan Sesi User</h4>
                <div className="space-y-1.5 text-slate-600">
                  <div className="flex justify-between"><span className="text-slate-400">Total Perangkat Login</span><span className="font-bold text-[#A97838]">{selectedSessionDetail.devices.length} Devices</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Perusahaan</span><span className="font-semibold text-slate-800">{selectedSessionDetail.company}</span></div>
                </div>
              </div>

              {/* Perangkat Aktif List */}
              <div className="space-y-3">
                <h4 className="font-bold text-slate-900 text-xs">Daftar Perangkat Aktif ({selectedSessionDetail.devices.length})</h4>
                
                {selectedSessionDetail.devices.map(dev => (
                  <div key={dev.sessionId} className="bg-white border border-[#ECECEC] p-4 rounded-xl shadow-2xs space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="font-bold text-slate-900 text-xs">{dev.device} • {dev.browser}</p>
                      <span className="text-[10px] font-bold text-[#20C05C] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">{dev.status}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-mono">IP: {dev.ip} • Lokasi: {dev.location}</p>
                    <p className="text-[10px] text-slate-600">Halaman Aktif: <span className="font-semibold text-[#A97838]">{dev.currentPage}</span></p>

                    <div className="pt-2 border-t border-slate-100 flex justify-end">
                      <button 
                        onClick={() => handleLogoutDevice(selectedSessionDetail.userId, dev.sessionId, dev.device)}
                        className="text-[10px] font-bold text-rose-600 hover:underline"
                      >
                        Logout Perangkat Ini
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Footer Drawer */}
            <div className="p-4 border-t border-[#ECECEC] bg-[#FAF8F5]">
              <Button 
                onClick={() => handleForceLogoutAll(selectedSessionDetail.userId, selectedSessionDetail.userName)}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold h-10 rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-sm"
              >
                <ShieldAlert className="w-4 h-4" /> Force Logout Semua Device
              </Button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
